import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { MetricFilter } from '../components/research-analytics/ResearchAnalyticsFilters';
import researchAnalyticsService, {
  ResearchAnalyticsArticlesResponse,
  ResearchAnalyticsSummary,
} from '../services/research/researchAnalytics.service';

type Filters = {
  search: string;
  dateFrom?: string;
  dateTo?: string;
  sort: string;
  sortDir: 'asc' | 'desc';
  page: number;
  pageSize: number;
  metricFilters: MetricFilter[];
};

const useResearchAnalytics = () => {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    dateFrom: undefined,
    dateTo: undefined,
    sort: 'createDate',
    sortDir: 'desc',
    page: 0,
    pageSize: 20,
    metricFilters: [],
  });

  const summaryQuery = useQuery<ResearchAnalyticsSummary>(
    ['researchAnalyticsSummary', filters.search, filters.dateFrom, filters.dateTo],
    () =>
      researchAnalyticsService.summary({
        search: filters.search || undefined,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      }),
    {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    }
  );

  const articlesQuery = useQuery<ResearchAnalyticsArticlesResponse>(
    [
      'researchAnalyticsArticles',
      filters.search,
      filters.dateFrom,
      filters.dateTo,
      filters.sort,
      filters.sortDir,
      filters.page,
      filters.pageSize,
      filters.metricFilters,
    ],
    () =>
      researchAnalyticsService.articles({
        search: filters.search || undefined,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        sort: filters.sort,
        sortDir: filters.sortDir,
        page: filters.page,
        pageSize: filters.pageSize,
        metricFilters:
          filters.metricFilters.length > 0
            ? JSON.stringify(
                filters.metricFilters.map(f => ({
                  field: f.field,
                  operator: f.operator,
                  value: f.value,
                }))
              )
            : undefined,
      }),
    {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search, page: 0 }));
  }, []);

  const setDateRange = useCallback((dateFrom?: string, dateTo?: string) => {
    setFilters(prev => ({ ...prev, dateFrom, dateTo, page: 0 }));
  }, []);

  const setSort = useCallback((sort: string, sortDir: 'asc' | 'desc') => {
    setFilters(prev => ({ ...prev, sort, sortDir }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setFilters(prev => ({ ...prev, pageSize, page: 0 }));
  }, []);

  const setMetricFilters = useCallback((metricFilters: MetricFilter[]) => {
    setFilters(prev => ({ ...prev, metricFilters, page: 0 }));
  }, []);

  return {
    filters,
    summary: summaryQuery.data,
    isSummaryLoading: summaryQuery.isFetching,
    articles: articlesQuery.data,
    isArticlesLoading: articlesQuery.isFetching,
    setSearch,
    setDateRange,
    setSort,
    setPage,
    setPageSize,
    setMetricFilters,
  };
};

export default useResearchAnalytics;
